
import React, { useState } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Pencil, Trash2, RotateCcw, UserX } from 'lucide-react'
import { User } from '@/types'
import { useToast } from '@/components/ui/use-toast'
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { supabase } from '@/integrations/supabase/client'
import UserModal from './UserModal'

interface UsersTableProps {
  users: User[];
  loading: boolean;
  fetchUsers: () => Promise<void>;
  stores: any[];
}

const UsersTable: React.FC<UsersTableProps> = ({
  users,
  loading,
  fetchUsers,
  stores,
}) => {
  const { toast } = useToast()
  const [userToDeactivate, setUserToDeactivate] = useState<User | null>(null)
  const [userToReactivate, setUserToReactivate] = useState<User | null>(null)
  const [userToDelete, setUserToDelete] = useState<User | null>(null)
  const [editingUser, setEditingUser] = useState<User | null>(null)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)

  const handleEditUser = (user: User) => {
    setEditingUser(user)
    setIsEditModalOpen(true)
  }

  const handleCloseEditModal = () => {
    setEditingUser(null)
    setIsEditModalOpen(false)
  }

  const handleConfirmDeactivate = async () => {
    if (!userToDeactivate) return;

    try {
      const { data, error } = await supabase.functions.invoke('admin-update-user', {
        body: {
          userId: userToDeactivate.id,
          userData: {
            status: 'inactive'
          }
        }
      });

      if (error) {
        throw new Error(`Erro ao desactivar utilizador: ${error.message}`);
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Erro ao desactivar utilizador');
      }

      toast({ title: 'Sucesso', description: 'Utilizador desactivado.' });
      await fetchUsers();
    } catch (err: any) {
      // Log the full error for debugging (only in console)
      console.error('User deactivation error:', err);

      // Provide user-friendly error message without exposing backend details
      toast({
        title: 'Erro',
        description: 'Não foi possível desactivar o utilizador. Tente novamente.',
        variant: 'destructive'
      });
    } finally {
      setUserToDeactivate(null);
    }
  };

  const handleConfirmReactivate = async () => {
    if (!userToReactivate) return;

    try {
      const { data, error } = await supabase.functions.invoke('admin-update-user', {
        body: {
          userId: userToReactivate.id,
          userData: {
            status: 'active'
          }
        }
      });

      if (error) {
        throw new Error(`Erro ao reativar utilizador: ${error.message}`);
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Erro ao reativar utilizador');
      }

      toast({ title: 'Sucesso', description: 'Utilizador reativado.' });
      await fetchUsers();
    } catch (err: any) {
      console.error('User reactivation error:', err);
      toast({
        title: 'Erro',
        description: 'Não foi possível reativar o utilizador. Tente novamente.',
        variant: 'destructive'
      });
    } finally {
      setUserToReactivate(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;

    try {
      const { data, error } = await supabase.functions.invoke('admin-delete-user-permanent', {
        body: {
          userId: userToDelete.id
        }
      });

      if (error) {
        throw new Error(`Erro ao eliminar utilizador: ${error.message}`);
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Erro ao eliminar utilizador');
      }

      toast({ title: 'Sucesso', description: 'Utilizador eliminado permanentemente.' });
      await fetchUsers();
    } catch (err: any) {
      console.error('User deletion error:', err);
      toast({
        title: 'Erro',
        description: 'Não foi possível eliminar o utilizador. Tente novamente.',
        variant: 'destructive'
      });
    } finally {
      setUserToDelete(null);
    }
  };
  
  if (loading) {
    return <div className="py-8 text-center text-gray-400">A carregar utilizadores...</div>;
  }
  
  if (!users || users.length === 0) {
    return <div className="py-8 text-center text-gray-400">Nenhum utilizador encontrado</div>;
  }

  return (
    <>
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-[100px] text-white">Avatar</TableHead>
          <TableHead className="text-white">Utilizador</TableHead>
          <TableHead className="text-white">Cargo</TableHead>
          <TableHead className="text-white">Pontos</TableHead>
          <TableHead className="text-white">Status</TableHead>
          <TableHead className="text-white">Loja Principal</TableHead>
          <TableHead className="text-white">Ações</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {users.map(user => (
          <TableRow key={user.id}>
            <TableCell>
              <div className="h-10 w-10 relative">
                <img
                  src={user.avatar_url || user.avatar}
                  alt={user.username || user.name} 
                  className="rounded-full w-full h-full object-cover"
                />
              </div>
            </TableCell>
            <TableCell className="font-medium">
              {user.username || user.name}
              <div className="text-gray-300 text-xs">{user.email || '—'}</div>
            </TableCell>
            <TableCell>{user.role}</TableCell>
            <TableCell>{user.points}</TableCell>
            <TableCell>{user.status}</TableCell>
            <TableCell>{stores?.find((s: any) => s.id === user.main_store_id)?.name || '—'}</TableCell>              
            <TableCell>
              <div className="flex gap-2">
                {/* Edit button - always available */}
                <Button
                  size="icon"
                  variant="outline"
                  onClick={() => handleEditUser(user)}
                  aria-label={`Editar utilizador ${user.username || user.name}`}
                >
                  <Pencil className="h-4 w-4" />
                </Button>

                {/* Status-dependent action buttons */}
                {user.status === 'active' ? (
                  // Deactivate button for active users
                  <Dialog open={!!userToDeactivate} onOpenChange={(open) => !open && setUserToDeactivate(null)}>
                    <DialogTrigger asChild>
                      <Button
                        size="icon"
                        variant="secondary"
                        onClick={() => setUserToDeactivate(user)}
                        aria-label={`Suspender conta de ${user.username || user.name}`}
                      >
                        <UserX className="h-4 w-4" />
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Confirmar Suspensão de Conta</DialogTitle>
                        <DialogDescription>
                          Tem a certeza que deseja suspender a conta do utilizador <strong>{userToDeactivate?.username || userToDeactivate?.name}</strong>? O utilizador não conseguirá fazer login até reativar a conta.
                        </DialogDescription>
                      </DialogHeader>
                      <DialogFooter className="flex justify-end gap-2">
                        <Button variant="outline" onClick={() => setUserToDeactivate(null)}>
                          Cancelar
                        </Button>
                        <Button variant="secondary" onClick={handleConfirmDeactivate}>
                          Suspender Conta
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                ) : (
                  // Reactivate and Delete buttons for inactive users
                  <>
                    <Dialog open={!!userToReactivate} onOpenChange={(open) => !open && setUserToReactivate(null)}>
                      <DialogTrigger asChild>
                        <Button
                          size="icon"
                          variant="default"
                          onClick={() => setUserToReactivate(user)}
                          aria-label={`Reativar conta de ${user.username || user.name}`}
                        >
                          <RotateCcw className="h-4 w-4" />
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Confirmar Reativação</DialogTitle>
                          <DialogDescription>
                            Tem a certeza que deseja reativar a conta do utilizador <strong>{userToReactivate?.username || userToReactivate?.name}</strong>? O utilizador voltará a conseguir fazer login.
                          </DialogDescription>
                        </DialogHeader>
                        <DialogFooter className="flex justify-end gap-2">
                          <Button variant="outline" onClick={() => setUserToReactivate(null)}>
                            Cancelar
                          </Button>
                          <Button variant="default" onClick={handleConfirmReactivate}>
                            Reativar Conta
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>

                    <Dialog open={!!userToDelete} onOpenChange={(open) => !open && setUserToDelete(null)}>
                      <DialogTrigger asChild>
                        <Button
                          size="icon"
                          variant="destructive"
                          onClick={() => setUserToDelete(user)}
                          aria-label={`Eliminar permanentemente ${user.username || user.name}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>⚠️ Eliminar Permanentemente</DialogTitle>
                          <DialogDescription>
                            <strong className="text-red-600">ATENÇÃO:</strong> Esta ação irá eliminar permanentemente o utilizador <strong>{userToDelete?.username || userToDelete?.name}</strong> de toda a base de dados, incluindo dados de autenticação. Esta ação é <strong>irreversível</strong>.
                          </DialogDescription>
                        </DialogHeader>
                        <DialogFooter className="flex justify-end gap-2">
                          <Button variant="outline" onClick={() => setUserToDelete(null)}>
                            Cancelar
                          </Button>
                          <Button variant="destructive" onClick={handleConfirmDelete}>
                            Eliminar Permanentemente
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </>
                )}
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>

    {/* Edit User Modal */}
    <UserModal
      isOpen={isEditModalOpen}
      onClose={handleCloseEditModal}
      editingUser={editingUser}
      onUserSaved={fetchUsers}
    />
  </>
  )
}

export default UsersTable
